import { MinusIcon } from '@heroicons/react/24/outline'
import type React from 'react'
import { useState } from 'react'
import type { SelectOption } from './MetadataHandling/CSVMetadataSharedSelect.js'
import { CSVMetadataSharedSelect } from './MetadataHandling/CSVMetadataSharedSelect.js'
import type { CSVColumnSelectorValidationState } from './MetadataHandling/CSVCommonState.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'

export type CustomMetadataState = {
  selectedColumns: (string | null)[]
}

interface CustomMetadataSectionProps {
  state: CustomMetadataState
  setState: React.Dispatch<React.SetStateAction<CustomMetadataState>>
  csvHeaders: string[]
}

export const CustomMetadataSection = (props: CustomMetadataSectionProps) => {
  return (
    <>
      <hr className="mt-2 mb-1" />
      <TooltipTrigger
        tooltip={'Custom Metadata can be created from arbitrary columns in your input csv. It can be used to add your own data to samples.'}
        withIcon={true}
      >
        <label className="mb-2 font-semibold">Custom Metadata</label>
      </TooltipTrigger>
      {props.state.selectedColumns.length === 0 && (
        <div className={'flex justify-center italic text-gray-500 text-sm'}>No Custom Metadata selected.</div>
      )}
      <div className="grid grid-cols-[auto_auto_1fr] gap-2 items-center">
        {props.state.selectedColumns.map((col, i) => {
          return <SelectionRow key={i} props={props} index={i} />
        })}
      </div>
      <div className={'flex justify-center'}>
        <button
          disabled={props.state.selectedColumns.includes(null)}
          className={'btn btn-primary my-4'}
          onClick={() => {
            props.setState((prev: CustomMetadataState) => {
              return {
                selectedColumns: [...prev.selectedColumns, null]
              }
            })
          }}
        >
          Add Entry
        </button>
      </div>
      <div className={'mb-8'} />
    </>
  )
}

interface SelectionRowProps {
  props: CustomMetadataSectionProps
  index: number
}

const SelectionRow = (props: SelectionRowProps) => {
  const [validationState, setValidationState] = useState<CSVColumnSelectorValidationState>('NO_DATA')
  const [selectedValue, setSelectedValue] = useState<SelectOption | null>(null)
  const reactSelectOptions = [
    {
      value: '',
      label: 'Select Column matching the criteria',
      rawKey: ''
    },
    ...props.props.csvHeaders.map((d: string) => ({
      value: d,
      label: d,
      rawKey: d
    }))
  ]

  const handleUpdate = (s: SelectOption | null) => {
    setSelectedValue(s)
    let effectiveValue: string | null = null
    if (s !== null && s.value !== '') {
      effectiveValue = s.value
      setValidationState('VALIDATED')
    }
    props.props.setState((prev: CustomMetadataState) => {
      const modified = [...prev.selectedColumns]
      modified[props.index] = effectiveValue
      return {
        selectedColumns: modified
      }
    })
  }

  return (
    <>
      <button
        className="btn btn-error btn-sm px-2 py-1 aspect-square flex items-center justify-center"
        onClick={() => {
          props.props.setState((prev) => {
            const updated = [...prev.selectedColumns]
            updated.splice(props.index, 1)
            return { selectedColumns: updated }
          })
        }}
      >
        <MinusIcon className="w-4 h-4" />
      </button>
      <CSVMetadataSharedSelect
        config={{ label: `Custom Data ${props.index}` }}
        selectedValue={selectedValue}
        options={reactSelectOptions}
        onChange={handleUpdate}
        validationState={validationState}
      />
    </>
  )
}
